import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { transcriptionApiKey } from '#/shared/lib/stt'
import { toast } from '#/shared/ui/sonner'

import type { DictationSession } from './dictation-support'
import { acquireSharedMic, micErrorKey, stopSharedMic } from './mic'
import type { ScribeSession } from './scribe-session'
import { startScribeSession } from './scribe-session'
import { playVoiceStart, playVoiceStop } from './voice-sounds'

/** One session's UI lifecycle — the phases are mutually exclusive, so a single
 *  value can't get stuck claiming both "recording" and "processing". */
type Phase = 'idle' | 'opening' | 'recording' | 'processing'

/** A ref always holding the latest `value`, so long-lived session handlers
 *  never close over a stale callback. */
function useLatestRef<T>(value: T): { current: T } {
  const ref = useRef(value)
  ref.current = value
  return ref
}

/** Preflight a mic tap: the mic APIs are disabled outside a secure context
 *  (plain HTTP on anything but localhost), and streaming needs the API key —
 *  toast what's missing instead of flashing on then erroring. Returns the key,
 *  or null when dictation can't start. */
function preflightDictation(t: (key: string) => string): string | null {
  if (!window.isSecureContext) {
    toast.error(t('voice.insecure'))
    return null
  }
  const apiKey = transcriptionApiKey()
  if (apiKey === undefined) {
    toast.error(t('voice.unsupported'))
    return null
  }
  return apiKey
}

/** Acquire the microphone and start a streaming session on it, toasting the
 *  failure and resolving to null when either step fails. */
async function openScribeSession(options: {
  apiKey: string
  onAutoStop: () => void
  onError: () => void
  t: (key: string) => string
}): Promise<{ mic: MediaStream; session: ScribeSession } | null> {
  let mic: MediaStream
  try {
    mic = await acquireSharedMic()
  } catch (error) {
    toast.error(options.t(micErrorKey(error)))
    return null
  }
  try {
    const session = await startScribeSession({
      apiKey: options.apiKey,
      mic,
      onAutoStop: options.onAutoStop,
      onError: options.onError,
    })
    return { mic, session }
  } catch {
    toast.error(options.t('voice.error'))
    return null
  }
}

/**
 * Speech-to-text streamed to ElevenLabs Scribe (the same engine as the live
 * call transcript) — the desktop engine behind {@link useVoiceDictation}.
 * `start` opens the microphone and begins streaming; the user speaks while the
 * live `MediaStream` drives a waveform, and nothing is typed yet. `stop` ends
 * listening and emits the final punctuated transcript to `onTranscript` exactly
 * once — usually near-instantly, since transcription happened while they spoke
 * (`processing` covers the brief wait for the trailing words to settle).
 */
export function useScribeDictation({
  onTranscript,
}: {
  /** Called once, with the final transcript, after the user stops recording. */
  onTranscript: (sessionText: string) => void
}): DictationSession {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('idle')
  const [stream, setStream] = useState<MediaStream | null>(null)

  const onTranscriptRef = useLatestRef(onTranscript)
  const tRef = useLatestRef(t)

  const sessionRef = useRef<ScribeSession | null>(null)
  // Bumped whenever a session ends; awaited work bails if it changed meanwhile
  // (the user stopped or we unmounted during an await). Also how a start still
  // opening learns it was cancelled — `phase` in a ref would be stale there.
  const genRef = useRef(0)
  const openingRef = useRef(false)

  // Hard cleanup WITHOUT a transcript: release the mic and drop the session.
  // Used for unmount, stop-during-open, and error paths — the finalize path
  // (user stop) emits the text instead.
  const teardown = useCallback(() => {
    genRef.current += 1
    sessionRef.current?.cancel()
    sessionRef.current = null
    stopSharedMic()
    setStream(null)
    setPhase('idle')
  }, [])

  // User stop (and silence auto-stop): release the mic immediately, then wait
  // for the session's final text and emit it once. The generation bump
  // invalidates the pending result if we unmount before it resolves, so
  // nothing is inserted after the surface is gone.
  const finalize = useCallback(async () => {
    const session = sessionRef.current
    if (!session) {
      // Stopped mid-open (mic not yet granted): just cancel the opening start.
      if (openingRef.current) teardown()
      return
    }
    const gen = (genRef.current += 1)
    sessionRef.current = null
    stopSharedMic()
    setStream(null)
    setPhase('processing')
    playVoiceStop()
    const text = await session.finalize()
    if (gen !== genRef.current) return // superseded (unmount / restart)
    setPhase('idle')
    if (text) onTranscriptRef.current(text)
  }, [teardown])

  const stop = useCallback(() => void finalize(), [finalize])
  // Session callbacks fire long after start ran — never hold a stale stop.
  const stopRef = useLatestRef(stop)

  const start = useCallback(async () => {
    if (openingRef.current || sessionRef.current) return
    const apiKey = preflightDictation((key) => tRef.current(key))
    if (apiKey === null) return
    openingRef.current = true
    const gen = (genRef.current += 1)
    setPhase('opening')

    const opened = await openScribeSession({
      apiKey,
      t: (key) => tRef.current(key),
      onAutoStop: () => stopRef.current(),
      onError: () => {
        // Transport died mid-recording: tell the user, then run the normal
        // stop flow so whatever was already transcribed still lands.
        toast.error(tRef.current('voice.error'))
        stopRef.current()
      },
    })
    openingRef.current = false

    if (!opened || gen !== genRef.current) {
      // Failed to open, or the user stopped mid-open (their stop already ran
      // `teardown`) — release whatever the aborted open still holds.
      opened?.session.cancel()
      if (gen === genRef.current) teardown()
      else stopSharedMic()
      return
    }
    sessionRef.current = opened.session
    setStream(opened.mic)
    setPhase('recording')
    playVoiceStart()
  }, [teardown])

  useEffect(() => () => teardown(), [teardown])

  return {
    recording: phase === 'recording',
    loading: phase === 'opening',
    processing: phase === 'processing',
    stream,
    start,
    stop,
  }
}
